import * as types from '../types.js';
import api from '../api.js';
import { refreshIntegrationEntityAlerts } from '../utils/integration-entity-alerts.js';
import { bindFormFieldTitleSync, syncFormFieldTitles } from '../utils/form-field-titles.js';
import {
	paintOutputOrderStorageArmBanner,
	refreshOutputOrderStorageArmCells,
} from './output-orders-storage-arm.js';
import {
	paintOutputOrderSimCells,
	refreshOutputOrderSimStock,
} from './output-orders-sim-stock.js';

/** Código fiscal de atendimento Televendas (#MT-A61F144) — label NF-e: Teleatendimento */
const OO_DEFAULT_SERVICE_CHANNEL = '3';

function _parseSialNum(s) {
	const c = String(s ?? '').replace(/\./g, '').replace(',', '.');
	const n = parseFloat(c);
	return isFinite(n) ? n : 0;
}

function _fmtBRL(v) {
	return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function recalcOutputOrderLineNumbers(root) {
	if (!root) return;
	const tbody = root.querySelector('.sial-items-scroll tbody');
	if (!tbody) return;
	let seq = 0;
	tbody.querySelectorAll('tr.row').forEach((row) => {
		const prodEl = row.querySelector('[id^="product_id-"]');
		if (!prodEl) return;
		const n = (prodEl.id.match(/\d+$/) || ['0'])[0];
		const itemNoEl = row.querySelector(`#item_no-${n}`);
		if (!itemNoEl) return;
		seq += 1;
		const numHidden = row.querySelector(`#number-${n}`);
		let display = seq;
		if (numHidden && numHidden.value !== '' && numHidden.value != null) {
			const internal = parseInt(numHidden.value, 10);
			if (!isNaN(internal))
				display = internal + 1;
		}
		itemNoEl.value = String(display);
	});
}

function recalcOutputOrderTotals(root) {
	if (!root) return;
	recalcOutputOrderLineNumbers(root);
	const tbody = root.querySelector('.sial-items-scroll tbody');
	const totalEl = root.querySelector('#oo-grand-total');
	if (!tbody) return;
	let grand = 0;
	tbody.querySelectorAll('tr.row').forEach((row) => {
		const qEl = row.querySelector('[id^="quantity-"]');
		if (!qEl) return;
		const n = (qEl.id.match(/\d+$/) || ['0'])[0];
		const qty = _parseSialNum(qEl.value);
		const price = _parseSialNum(row.querySelector(`#unit_price-${n}`)?.value);
		const disc = _parseSialNum(row.querySelector(`#discount-${n}`)?.value);
		const lineTotal = qty * price * (1 - disc / 100);
		const ti = row.querySelector(`#item_total-${n}`);
		if (ti)
			ti.value = (qty > 0 || price > 0) ? _fmtBRL(lineTotal) : '';
		grand += lineTotal;
	});
	if (totalEl)
		totalEl.textContent = grand > 0 ? `Total do Pedido: ${_fmtBRL(grand)}` : '';
}

function applyNewOutputOrderDefaults(formset) {
	const vals = formset?.form?.getValues?.() || {};
	if (vals.id != null && vals.id !== '') return;
	const sc = formset?.form?.getFieldBySelector?.('#service_channel');
	if (sc && (sc.getValue?.() == null || sc.getValue?.() === ''))
	{
		sc.setValue(OO_DEFAULT_SERVICE_CHANNEL);
		sc.refresh?.();
	}
	const pm = formset?.form?.getFieldBySelector?.('#payment_method');
	pm?.eraseValue?.();
	pm?.resetAuto?.();
	pm?.refresh?.();
}

/** Travamento quando há saída ativa: só transportador, condição, tipo/valor frete editáveis. */
function applyOutputOrderEditLock(fs)
{
	const form = document.querySelector('#order');
	const banner = document.querySelector('#oo-lock-banner');
	if (!form) return;
	const vals = fs.form.getValues();
	const locked = vals.edit_locked_by_output === true || vals.edit_locked_by_output === 1
		|| vals.edit_locked_by_output === '1' || String(vals.edit_locked_by_output).toLowerCase() === 'true';
	form.classList.toggle('output-order--edit-locked', locked);
	if (banner) banner.style.display = locked ? 'block' : 'none';
	const toolbar = form.querySelector('.oo-footer span:first-child');
	const rowImgs = toolbar ? toolbar.querySelectorAll('img') : [];
	const allowSelectors = ['#carrier_id', '#payment_method', '#payment_term_id', '#freight_type', '#freight_value'];
	if (!locked)
	{
		form.querySelectorAll('[data-oo-lock-disabled]').forEach((el) =>
		{
			el.removeAttribute('data-oo-lock-disabled');
			el.removeAttribute('disabled');
		});
		rowImgs.forEach((img) => { img.style.pointerEvents = ''; img.style.opacity = ''; });
		return;
	}
	form.querySelectorAll('input, select, textarea, button').forEach((el) =>
	{
		if (el.type === 'hidden') return;
		if (el.tagName === 'BUTTON' && (el.classList.contains('bn_Escape') || el.classList.contains('bn_Enter'))) return;
		if (allowSelectors.some((sel) => { const n = form.querySelector(sel); return n && el === n; })) return;
		if (el.closest('.oo-lock-allow')) return;
		el.setAttribute('data-oo-lock-disabled', '1');
		el.setAttribute('disabled', 'disabled');
	});
	rowImgs.forEach((img) => { img.style.pointerEvents = 'none'; img.style.opacity = '0.45'; });
}

export default {

	html: `
		<style>
			#order.oo-order-form .oo-order-header-scroll {
				flex-shrink: 0;
				overflow-x: hidden;
				padding-right: 0.2rem;
			}
			#order.oo-order-form .oo-order-tail {
				flex-shrink: 0;
			}
			#order.oo-order-form .oo-form-row {
				display: flex;
				flex-wrap: wrap;
				gap: 0.45rem 0.75rem;
				align-items: flex-end;
				margin-bottom: 0.45rem;
			}
			#order.oo-order-form .oo-form-row label {
				margin-right: 0 !important;
				margin-bottom: 0 !important;
			}
			#order.oo-order-form #company_id		{ width: 9rem; }
			#order.oo-order-form #id				{ width: 5rem; text-align: right; }
			#order.oo-order-form #reference		{ width: 8rem; }
			#order.oo-order-form #created_on,
			#order.oo-order-form #created_by,
			#order.oo-order-form #updated_on,
			#order.oo-order-form #updated_by		{ width: 9.5rem; }
			#order.oo-order-form label.sial-field-address {
				flex: 1 1 24rem;
				min-width: min(100%, 18rem);
				max-width: 100%;
			}
			#order.oo-order-form label.sial-field-address input {
				width: 100% !important;
			}
			#order.oo-order-form label.sial-field-party-customer {
				flex: 1 1 22rem;
				min-width: min(100%, 16rem);
				max-width: 100%;
			}
			#order.oo-order-form label.sial-field-party-customer input {
				width: 100% !important;
				font-weight: 700;
				color: #475569;
			}
			#order.oo-order-form label.sial-field-party-customer input:disabled {
				color: #64748b;
				opacity: 0.85;
			}
			#order.oo-order-form label.sial-field-party-nick {
				flex: 1 1 14rem;
				min-width: min(100%, 11rem);
				max-width: 100%;
			}
			#order.oo-order-form label.sial-field-party-nick input {
				width: 100% !important;
			}
			#order.oo-order-form #carrier_id		{ width: 12rem; }
			#order.oo-order-form .oo-delivery-info { font-size: 0.82em; color: var(--muted,#666); margin-top: 0.2rem; }
			#order.oo-order-form #program_id		{ width: 11rem; }
			#order.oo-order-form #service_channel,
			#order.oo-order-form #payment_method,
			#order.oo-order-form #freight_type,
			#order.oo-order-form #status			{ width: 9rem; }
			#order.oo-order-form #payment_term_id	{ width: 7rem; }
			#order.oo-order-form #freight_value,
			#order.oo-order-form #discount			{ width: 5rem; text-align: right; }
			#order.oo-order-form #expected_delivery_date { width: 9rem; }
			#order.oo-order-form .sial-items-scroll .oo-col-item-no { width: 3.2rem; text-align: center; }
			#order.oo-order-form .sial-items-scroll .oo-col-item-no input { text-align: center; }
			#order.oo-order-form .sial-items-scroll .oo-col-product { width: 22%; }
			#order.oo-order-form .sial-items-scroll .oo-col-arm { width: 8.5%; }
			#order.oo-order-form .sial-items-scroll .oo-col-arm input {
				font-size: 0.78rem;
				font-weight: 600;
				text-align: center;
				padding-left: 0.15rem;
				padding-right: 0.15rem;
			}
			#order.oo-order-form .sial-items-scroll td.oo-arm--clim,
			#order.oo-order-form .sial-items-scroll td.oo-arm--clim input.oo-arm--clim {
				background: #ecfdf5 !important;
				color: #065f46;
			}
			#order.oo-order-form .sial-items-scroll td.oo-arm--refrig,
			#order.oo-order-form .sial-items-scroll td.oo-arm--refrig input.oo-arm--refrig {
				background: #e0f2fe !important;
				color: #075985;
			}
			#order.oo-order-form .sial-items-scroll tr.oo-row--arm-mixed {
				outline: 1px solid #f59e0b;
				outline-offset: -1px;
			}
			#order.oo-order-form .sial-items-scroll .oo-col-lot { width: 8%; }
			#order.oo-order-form .sial-items-scroll .oo-col-unit { width: 7%; }
			#order.oo-order-form .sial-items-scroll .oo-col-qty { width: 6%; }
			#order.oo-order-form .sial-items-scroll .oo-col-sim { width: 7%; }
			#order.oo-order-form .sial-items-scroll .oo-col-sim input { text-align: right; }
			#order.oo-order-form .sial-items-scroll .oo-col-sit { width: 11%; }
			#order.oo-order-form .sial-items-scroll td.oo-sim-neg input,
			#order.oo-order-form .sial-items-scroll td.oo-sim-neg { background: #fecaca !important; font-weight: 700; }
			#order.oo-order-form .sial-items-scroll td.oo-sim-warn input,
			#order.oo-order-form .sial-items-scroll td.oo-sim-warn { background: #fef08a !important; }
			#order.oo-order-form .sial-items-scroll .oo-col-disc { width: 6%; }
			#order.oo-order-form .sial-items-scroll .oo-col-price { width: 8%; }
			#order.oo-order-form .sial-items-scroll .oo-col-total { width: 8%; }
			#order.oo-order-form label.notes {
				display: flex;
				flex-direction: column;
				width: 100%;
				margin: 0.4rem 0 0 0 !important;
			}
			#order.oo-order-form label.notes textarea {
				width: 100%;
				box-sizing: border-box;
			}
			#order.oo-order-form #book_observation { min-height: 2.5rem; }
			#order.oo-order-form .oo-grand-total {
				text-align: right; font-weight: 600; font-size: 0.88rem;
				padding: 0.35rem 0.25rem 0; color: var(--color-text, #222);
				flex-shrink: 0;
			}
			#order.oo-order-form .oo-footer {
				display: flex;
				justify-content: space-between;
				align-items: center;
				flex-shrink: 0;
				margin-top: 0.45rem;
				padding-top: 0.65rem;
				border-top: 1px solid var(--color-border, #ccc);
				gap: 0.75rem;
				flex-wrap: wrap;
			}
			#oo-lock-banner { margin: 0 0 0.75rem 0; padding: 0.5rem 0.65rem; background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 4px; color: #475569; }
			#oo-integration-alert-banner { display: none; }
			#oo-storage-arm-banner { display: none; margin: 0 0 0.5rem 0; padding: 0.55rem 0.7rem; border-radius: 4px; font-size: 0.88rem; line-height: 1.35; }
			#oo-storage-arm-banner.oo-storage-arm-banner--warn { background: #fef3c7; border: 1px solid #f59e0b; color: #92400e; font-weight: 600; }
			#order.oo-order-form.output-order--edit-locked label:not(.oo-lock-allow) { color: #94a3b8; }
		</style>
		<form id="order" class="form oo-order-form sial-modal-form" autocomplete="off">
			<input type="hidden" id="edit_locked_by_output" value="0">
			<p id="oo-lock-banner" class="fsxx" style="display:none">Este pedido possui saída gerada ou entrada de devolução ativa (não cancelada): os campos ficam bloqueados, exceto <strong>Pagamento</strong>, <strong>Condição</strong>, <strong>Transportador</strong>, <strong>Tipo de frete</strong> e <strong>Valor do frete</strong>. A edição plena volta quando todas as saídas/entradas vinculadas forem canceladas.</p>
			<div id="oo-integration-alert-banner" role="status" aria-live="polite"></div>
			<datalist id="companies">
			</datalist>
			<datalist id="customers">
			</datalist>
			<datalist id="customers_nick">
			</datalist>
			<datalist id="carriers">
			</datalist>
			<datalist id="programs">
			</datalist>
			<datalist id="payment_terms">
			</datalist>
			<datalist id="products">
			</datalist>
			<datalist id="units">
			</datalist>
			<datalist id="users">
			</datalist>
			<datalist id="delivery_addresses">
			</datalist>
			<div class="oo-order-header-scroll">
			<div class="oo-form-row">
				<label title="Empresa detentora do registro">Empresa<sup>*</sup>
					<input id="company_id" type="text" list="companies" required>
				</label>
				<label title="Identificador do pedido">Pedido
					<input id="id" type="text" disabled>
				</label>
				<label title="Código de referência externa">Referência
					<input id="reference" type="text">
				</label>
				<label title="Data e hora de criação do pedido">Data Criação
					<input id="created_on" type="text" disabled>
				</label>
				<label title="Usuário criador do pedido">Criado Por
					<input id="created_by" type="text" list="users" disabled>
				</label>
				<label title="Data e hora de alteração do pedido">Data Alteração
					<input id="updated_on" type="text" disabled>
				</label>
				<label title="Último usuário a alterar o pedido">Alterado Por
					<input id="updated_by" type="text" list="users" disabled>
				</label>
				<label title="Status do pedido">Status<sup>*</sup>
					<input id="status" type="text" list="order_status" value="10" required>
				</label>
				<label title="Este pedido é uma devolução?">Devolução
					<input id="is_return" type="checkbox">
				</label>
				<label title="Pedido emergencial (destaque operacional)">Emergencial
					<input id="is_emergency" type="checkbox">
				</label>
			</div>
			<div class="oo-form-row oo-form-row-party">
				<label class="sial-field-party sial-field-party-nick" title="Apelido do cliente — preenche o Cliente ao selecionar">Apelido
					<input id="customer_nick" type="text" list="customers_nick" placeholder="Buscar por apelido">
				</label>
				<label class="sial-field-party sial-field-party-customer" title="Cliente do pedido — nome / CNPJ / CPF">Cliente<sup>*</sup>
					<input id="customer_id" type="text" list="customers" required>
				</label>
				<label class="sial-field-party sial-field-address" title="Endereço de entrega para este pedido">Endereço Entrega
					<input id="delivery_address_id" type="text" list="delivery_addresses" placeholder="Selecione o endereço">
				</label>
				<label title="Canal de atendimento do cliente">Atendimento<sup>*</sup>
					<input id="service_channel" type="text" list="service_channels" required>
				</label>
				<label class="oo-lock-allow" title="Transportador do pedido">Transportador
					<input id="carrier_id" type="text" list="carriers">
				</label>
				<label title="Programa (estrutura de estoque a considerar)">Programa
					<input id="program_id" type="text" list="programs">
				</label>
				<label class="oo-lock-allow" title="Tipo de frete">Tipo Frete
					<input id="freight_type" type="text" list="freight_types">
				</label>
				<label class="oo-lock-allow" title="Valor do frete">Valor Frete
					<input id="freight_value" type="text">
				</label>
				<label title="Desconto sobre o valor do pedido">% Desc
					<input id="discount" type="text">
				</label>
				<label title="Data de previsão de entrega do pedido">Prev. Entrega
					<input id="expected_delivery_date" type="date">
				</label>
			</div>
			<div class="oo-form-row oo-form-row-payment">
				<label class="oo-lock-allow" title="Forma de pagamento">Pagamento<sup>*</sup>
					<input id="payment_method" type="text" list="payment_methods" required>
				</label>
				<label class="oo-lock-allow" title="Condição de pagamento">Condição<sup>*</sup>
					<input id="payment_term_id" type="text" list="payment_terms" required>
				</label>
			</div>
			</div>
			<div class="sial-items-panel">
				<div class="sial-items-title">Itens do pedido</div>
				<div id="oo-storage-arm-banner" role="status" aria-live="polite"></div>
				<div class="sial-items-scroll">
					<table>
						<thead>
							<tr>
								<th class="sial-col-chk"></th>
								<th class="oo-col-item-no">Nº item</th>
								<th class="oo-col-product">Produto<sup>*</sup></th>
								<th class="oo-col-arm" title="Tipo de armazenamento do produto (cadastro: Refrigerado ou climatizado)">Arm.</th>
								<th class="oo-col-lot">Lote</th>
								<th class="oo-col-unit">Unidade<sup>*</sup></th>
								<th class="oo-col-qty tar">Qtde<sup>*</sup></th>
								<th class="oo-col-sim tar" title="Pré-reserva de pedidos aprovados neste SKU (não é reserva física)">Pré-reserva</th>
								<th class="oo-col-sim tar" title="Saldo livre operacional menos pré-reservas (SKU)">Saldo sim.</th>
								<th class="oo-col-sit" title="Situação do saldo simulado do SKU">Situação estoque</th>
								<th class="oo-col-disc tar">% Desc</th>
								<th class="oo-col-price tar">Preço Un<sup>*</sup></th>
								<th class="oo-col-total tar">Subtotal</th>
							</tr>
						</thead>
						<tbody>
							<tr class="row" data-size="10">
								<td class="sial-col-chk">
									<input type="hidden" id="number-0" value="">
									<input id="checkbox-0" type="checkbox">
								</td>
								<td class="oo-col-item-no" title="Número sequencial do item">
									<input id="item_no-0" class="item_no" type="text" readonly tabindex="-1">
								</td>
								<td title="Produto">
									<input id="product_id-0" class="product" type="text" list="products" required>
								</td>
								<td class="oo-col-arm" title="Tipo de armazenamento (Climatizado = sem flag Refrigerado no cadastro)">
									<input id="storage_arm-0" class="storage_arm" type="text" readonly tabindex="-1" aria-label="Tipo de armazenamento">
								</td>
								<td title="Lote do produto">
									<input id="lot_id-0" class="lot" type="text" list="lots-0">
								</td>
								<td title="Unidade de venda">
									<input id="unit_id-0" class="unit" type="text" list="units" required>
								</td>
								<td class="tar" title="Quantidade">
									<input id="quantity-0" class="quantity" type="text" required>
								</td>
								<td class="tar oo-col-sim" title="Pré-reserva de pedidos aprovados neste SKU (não é reserva física)">
									<input id="pre_reserved_by_approved_orders-0" class="pre_reserved" type="text" readonly tabindex="-1">
								</td>
								<td class="tar oo-col-sim" title="Saldo livre operacional menos pré-reservas (SKU)">
									<input id="simulated_available_quantity-0" class="sim_available" type="text" readonly tabindex="-1">
								</td>
								<td class="oo-col-sit" title="Situação do saldo simulado do SKU">
									<input id="stock_situation-0" class="stock_situation" type="text" readonly tabindex="-1">
								</td>
								<td class="tar" title="Desconto adicional sobre o valor deste item">
									<input id="discount-0" class="discount" type="text">
								</td>
								<td class="tar" title="Preço unitário do produto">
									<input id="unit_price-0" class="price" type="text" required>
								</td>
								<td class="tar oo-col-total" title="Subtotal da linha (qtde × preço unit., com % desc. do item)">
									<input id="item_total-0" class="item_total" type="text" readonly tabindex="-1">
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
			<div class="oo-order-tail">
				<div id="oo-grand-total" class="oo-grand-total"></div>
				<label class="notes" title="Observações sobre o pedido">Observações
					<textarea id="notes" rows="2"></textarea>
				</label>
				<label class="notes" title="Texto para livro eletrônico / NF-e quando aplicável">Obs. livro eletrônico (NF-e)
					<textarea id="book_observation" rows="2"></textarea>
				</label>
			</div>
			<div class="oo-footer">
				<span>
					<img class="bn_Invert" src="img/check.svg" title="Inverter caixas de seleção">
					<img class="bn_Clear" src="img/clear.svg" title="Apagar conteúdo das linhas selecionadas">
					<img class="bn_Insert" src="img/add.svg" title="Anexar uma nova linha">
					<img class="bn_Remove" src="img/remove.svg" title="Remover linhas selecionadas">
					<img class="bn_MoveUp" src="img/up.svg" title="Mover linhas selecionadas para cima">
					<img class="bn_MoveDn" src="img/down.svg" title="Mover linhas selecionadas para baixo">
				</span>
				<span>
					<button class='bn_Escape button'>Fechar</button>
					<button class='bn_Enter button'>Salvar</button>
				</span>
			</div>
		</form>
	`,

	form: 
	{
		selector: '#order',
		init(formset)	// Must not be async as init seq must be preserved
		{
			formset.loadList('#companies', 'companies', { labels: ['nick'] });
			formset.loadList('#programs', 'programs', { labels: ['name'] });
			formset.loadList('#users', 'users');
			Object.getPrototypeOf(this).init.call(this);

			// ── Endereço de entrega: carrega ao mudar customer_id ─────────────────
			const sel = '#order';
			const formRoot = formset.dialog?.getNode?.()?.querySelector(sel) || document.querySelector(sel);
			bindFormFieldTitleSync(formRoot);
			let _deliveryAddrs = [];

			const ooGet = id => document.querySelector(`${sel} #${id}`);

			const _formatDeliveryAddrLabel = (a) => {
				const tipo = String(a?.name || '').trim();
				const logradouro = [a?.street, a?.number, a?.complement].filter(Boolean).join(', ');
				const bairro = a?.district ? String(a.district).trim() : '';
				const cityUf = [a?.city, a?.state].filter(Boolean).join('/');
				const cep = a?.zipcode ? String(a.zipcode).trim() : '';
				const parts = [];
				if (tipo)
					parts.push(tipo);
				if (logradouro)
					parts.push(logradouro);
				if (bairro)
					parts.push(bairro);
				if (cityUf)
					parts.push(cityUf);
				if (cep)
					parts.push(`CEP ${cep}`);
				return parts.join(' — ') || tipo || 'Endereço';
			};

			const _getDeliveryAddressField = () =>
				formset?.form?.getFieldBySelector?.('#delivery_address_id');

			const _populateDeliveryDl = (addresses) => {
				_deliveryAddrs = addresses || [];
				const dl = ooGet('delivery_addresses');
				if (!dl) return;
				dl.innerHTML = '';
				_deliveryAddrs.forEach(a => {
					const opt = document.createElement('option');
					opt.value = String(a.id);
					opt.textContent = _formatDeliveryAddrLabel(a);
					dl.appendChild(opt);
				});
				_getDeliveryAddressField()?.refresh?.();
			};

			const _setDeliveryAddressId = (addressId) => {
				const field = _getDeliveryAddressField();
				if (!field) return;
				if (addressId == null || addressId === '') {
					field.setValue(-1);
				} else {
					field.setValue(Number(addressId));
				}
				field.refresh();
			};

			const _autoSelectDeliveryAddr = (currentId) => {
				// Aceita id legado -1/-2 (API normaliza; front ainda pode receber de pedidos antigos).
				const normId = (id) => {
					if (id == null || id === '') return id;
					const n = Number(id);
					if (n === -1) return 2000000001;
					if (n === -2) return 2000000002;
					return n;
				};
				if (currentId != null && currentId !== '') {
					const want = normId(currentId);
					const found = _deliveryAddrs.find(a => Number(a.id) === Number(want));
					if (found) {
						_setDeliveryAddressId(found.id);
						return;
					}
				}
				const field = _getDeliveryAddressField();
				const cur = field?.getValue?.();
				if (cur != null && cur !== '' && cur !== -1 && cur !== '-1') return;
				const def = _deliveryAddrs.find(a => a.is_default);
				if (def) {
					_setDeliveryAddressId(def.id);
					return;
				}
				if (_deliveryAddrs.length === 1) {
					_setDeliveryAddressId(_deliveryAddrs[0].id);
				}
			};

			const _resolveCompanyId = () => {
				const v = formset?.form?.getFieldByName?.('company_id')?.getValue?.();
				return v != null && String(v).trim() !== '' ? String(v).trim() : null;
			};

			/** Id numérico do cliente (datalist), não o rótulo CNPJ/nome exibido no input. */
			const _resolveCustomerId = () => {
				const v = formset?.form?.getFieldByName?.('customer_id')?.getValue?.();
				if (v == null || v === '' || v === -1 || v === '-1') return null;
				const n = Number(v);
				return Number.isInteger(n) && n > 0 ? n : null;
			};

			const _loadDeliveryAddresses = async (companyId, customerId, currentAddressId = null) => {
				if (!companyId || !customerId) {
					_populateDeliveryDl([]);
					_setDeliveryAddressId(null);
					return;
				}
				try {
					const rows = await api.call(api.list_customer_delivery_addresses, companyId, customerId, true);
					_populateDeliveryDl(Array.isArray(rows) ? rows : []);
					_autoSelectDeliveryAddr(currentAddressId);
				} catch (_) {
					_populateDeliveryDl([]);
				}
			};

			const _scheduleLoadDeliveryAddresses = (resetSelection = true) => {
				if (resetSelection) _setDeliveryAddressId(null);
				const compId = _resolveCompanyId();
				const custId = _resolveCustomerId();
				if (custId) {
					_loadDeliveryAddresses(compId, custId, null);
				} else {
					_populateDeliveryDl([]);
				}
			};

			// Recarregar endereços ao mudar empresa ou cliente (change + blur após datalist)
			ooGet('company_id')?.addEventListener('change', () => _scheduleLoadDeliveryAddresses(true));
			ooGet('customer_id')?.addEventListener('change', () => _scheduleLoadDeliveryAddresses(true));
			ooGet('customer_id')?.addEventListener('blur', () => {
				setTimeout(() => _scheduleLoadDeliveryAddresses(false), 0);
				setTimeout(() => {
					formset?.form?.getFieldBySelector?.('#payment_method')?.syncFromCustomer?.();
				}, 0);
			});

			// Expor para afterEditSetValues
			this._loadDeliveryAddresses = _loadDeliveryAddresses;

			const ooRoot = formRoot;
			if (ooRoot)
			{
				const tbl = ooRoot.querySelector('.sial-items-scroll table');
				if (tbl)
				{
					tbl.addEventListener('input', () => {
						recalcOutputOrderTotals(ooRoot);
						paintOutputOrderStorageArmBanner(ooRoot);
					});
				}
				setTimeout(() => {
					applyNewOutputOrderDefaults(formset);
					recalcOutputOrderTotals(ooRoot);
					refreshOutputOrderStorageArmCells(ooRoot);
					refreshOutputOrderSimStock(ooRoot);
				}, 350);
			}
		},
		afterEditSetValues(fs)
		{
			if (fs?.hasTables?.())
				fs.setTableHeight(10);
			const root = fs?.dialog?.getNode?.()?.querySelector(this.selector);
			setTimeout(() => {
				const formRoot = root || document.querySelector(this.selector);
				recalcOutputOrderTotals(formRoot);
				paintOutputOrderSimCells(formRoot);
				syncFormFieldTitles(formRoot);
				const valsEarly = fs.form.getValues?.() || {};
				refreshOutputOrderStorageArmCells(formRoot, valsEarly.company_id || null);
				refreshOutputOrderSimStock(formRoot, {
					companyId: valsEarly.company_id || null,
					programId: valsEarly.program_id != null && valsEarly.program_id !== ''
						? Number(valsEarly.program_id)
						: null,
				});
			}, 0);
			applyOutputOrderEditLock(fs);
			// Carregar endereços do cliente do pedido que está sendo editado
			const vals = fs.form.getValues?.() || {};
			const compId = vals.company_id;
			const custId = vals.customer_id ? Number(vals.customer_id) : null;
			const addrId = vals.delivery_address_id ? Number(vals.delivery_address_id) : null;
			// Espelhar Apelido ← Cliente (datalists podem carregar após setValues)
			if (custId != null && !Number.isNaN(custId) && custId >= 0)
			{
				setTimeout(() => {
					const nick = fs.form.getFieldByName?.('customer_nick');
					if (!nick) return;
					const cur = nick.getValue?.();
					if (Number(cur) === custId) {
						nick.refresh?.();
						return;
					}
					nick.setValue(custId);
					nick.refresh?.();
				}, 50);
			}
			if (this._loadDeliveryAddresses && custId) {
				this._loadDeliveryAddresses(compId, custId, addrId);
			}
			const orderId = vals.id;
			if (compId && orderId != null && orderId !== '')
			{
				refreshIntegrationEntityAlerts({
					formSelector: '#order',
					bannerId: 'oo-integration-alert-banner',
					companyId: compId,
					entityType: 'output_order',
					entityId: orderId,
				});
			}
		},
	},
	
	fields: [
		{ properties: { selector: '#edit_locked_by_output' }, types: [types.pubsub()] },
		{ properties: { selector: '#company_id' }, types: [types.pubsub(), types.datalist()] },
		{ properties: { selector: '#id' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#reference' }, types: [types.caps()] },
		{ properties: { selector: '#created_on' }, types: [types.datetime()] },
		{ properties: { selector: '#created_by' }, types: [types.datalist()] },
		{ properties: { selector: '#updated_on' }, types: [types.datetime()] },
		{ properties: { selector: '#updated_by' }, types: [types.datalist()] },
		{ properties: { selector: '#customer_nick' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('customers', { labels: ['nick', 'cnpj', 'cpf'], filter: (r) => Boolean(r?.nick && String(r.nick).trim()) }), types.obs_sync_customer_peer('customer_id')] },
		{ properties: { selector: '#customer_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('customers', { labels: ['name', 'cnpj', 'cpf'] }), types.obs_sync_customer_peer('customer_nick')] },
		{ properties: { selector: '#delivery_address_id' }, types: [types.datalist(), types.number(0)] },
		{ properties: { selector: '#carrier_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('carriers', { labels: ['nick'] })] },
		{ properties: { selector: '#program_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('programs', { labels: ['name'] })] },
		{ properties: { selector: '#service_channel' }, types: [types.opt_service_channel(), types.datalist()] },
		{ properties: { selector: '#payment_method' }, types: [types.pubsub(), types.opt_payment_method(), types.datalist(), types.observer('customer_id'), types.obs_customer_payment_method()] },
		{ properties: { selector: '#payment_term_id' }, types: [types.pubsub(), types.datalist(), types.observer(['company_id', 'customer_id']), types.obs_payment_term()] },
		{ properties: { selector: '#freight_type' }, types: [types.opt_freight_type(), types.datalist()] },
		{ properties: { selector: '#freight_value' }, types: [types.number(2)] },
		{ properties: { selector: '#discount' }, types: [types.pubsub(), types.observer('customer_id'), types.obs_discount(), types.number(2)] },
		{ properties: { selector: '#expected_delivery_date' }, types: [types.date()] },
		{ properties: { selector: '#notes' }, types: [types.textarea()] },
		{ properties: { selector: '#book_observation' }, types: [types.textarea()] },
		{ properties: { selector: '#is_return' }, types: [types.checkbox()] },
		{ properties: { selector: '#is_emergency' }, types: [types.checkbox()] },
		{ properties: { selector: '#status' }, types: [types.opt_output_order_status(), types.datalist()] },
		{ properties: { selector: '#number-0' }, types: [] },
		{ properties: { selector: '#item_no-0' }, types: [] },
		{ properties: { selector: '#checkbox-0' }, types: [types.checkbox()] },
		{ properties: { selector: '#product_id-0' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('products', { labels: ['sku', 'name'] })] },
		{ properties: { selector: '#storage_arm-0' }, types: [types.pubsub(), types.observer('product_id-0'), types.obs_storage_arm()] },
		{ properties: { selector: '#lot_id-0' }, types: [types.pubsub(), types.datalist(), types.observer('product_id-0'), types.obs_load_options('lots', { labels: ['lot_number', 'id'], sep: ' · ', key: 'company_id', range: 1 })] },
		{ properties: { selector: '#unit_id-0' }, types: [types.pubsub(), types.datalist(), types.observer(['company_id', 'product_id-0']), types.obs_unit()] },
		{ properties: { selector: '#quantity-0' }, types: [types.pubsub(), types.observer('product_id-0'), types.obs_stock(), types.number(0, 2)]},
		{ properties: { selector: '#pre_reserved_by_approved_orders-0' }, types: [types.number(0)] },
		{ properties: { selector: '#simulated_available_quantity-0' }, types: [types.pubsub(), types.observer(['product_id-0', 'program_id']), types.obs_output_order_sim_stock(), types.number(0)] },
		{ properties: { selector: '#stock_situation-0' }, types: [] },
		{ properties: { selector: '#discount-0' }, types: [types.pubsub(), types.number(2)] },
		{ properties: { selector: '#unit_price-0' }, types: [types.pubsub(), types.observer(['product_id-0', 'customer_id']), types.obs_price(), types.number(4)] },		// types.price sets the price and the discount (considera customer_prices e price_list)
		{ properties: { selector: '#item_total-0' }, types: [] },
	]

};

